import { ReactNode } from 'react';
import {
  Typography,
  TextField,
  Box,
  TextFieldProps,
  SxProps,
} from 'components/base';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';
import { error as errorColors, other } from 'assets/colors';
import { textFieldLabelStyles, LineItemStyledTextarea } from './styles';

type CustomTextFieldProps<T extends FieldValues> = TextFieldProps & {
  name: Path<T>;
  label?: string;
  type?: 'text' | 'textarea' | 'email' | 'number' | 'search' | 'password';
  placeholder?: string;
  control?: Control<T>;
  caption?: string;
  marginBottom?: number | string;
  width?: number | string;
  startAdornment?: ReactNode;
  showError?: boolean;
  shrink?: boolean;
  forceShrink?: boolean;
  textColor?: string;
  textColorInput?: string;
  showNegativeValueIndicator?: boolean;
  endAdornment?: ReactNode;
  boxWrapperSx?: SxProps;
};

export const FormTextField = <T extends FieldValues>({
  name,
  label,
  required,
  hiddenLabel = true,
  shrink = true,
  type = 'text',
  control,
  placeholder,
  caption,
  marginBottom,
  multiline,
  minRows = 1.3,
  maxRows = 1.1,
  width,
  startAdornment,
  showError,
  disabled,
  forceShrink,
  autoComplete = 'off',
  textColor,
  textColorInput = errorColors.main,
  showNegativeValueIndicator,
  endAdornment,
  boxWrapperSx,
  ...rest
}: CustomTextFieldProps<T>) => (
  <Box
    data-testid={`form-input-${name}-box`}
    sx={{ mb: marginBottom, ...boxWrapperSx }}
  >
    {label && !hiddenLabel && (
      <Typography
        variant="subtitle2"
        marginBottom="3px"
        sx={{ color: 'normalText.light', gap: '8px' }}
      >
        <label>
          {label} {required && '*'}{' '}
        </label>
      </Typography>
    )}
    {caption && (
      <Typography
        variant="body2"
        sx={{ marginBottom: '5px' }}
        fontSize={14}
        fontWeight={400}
        color="normalText.dark"
      >
        {caption}
      </Typography>
    )}

    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { invalid, error } }) => (
        <>
          {type === 'textarea' ? (
            <LineItemStyledTextarea
              {...field}
              id={name}
              name={name}
              placeholder={placeholder}
              minRows={minRows}
              maxRows={maxRows}
              autoComplete={autoComplete}
              disabled={disabled}
              data-testid={`form-${name}-text-area-field`}
            />
          ) : (
            <TextField
              {...field}
              hiddenLabel={hiddenLabel}
              type={type}
              id={name}
              InputLabelProps={{
                shrink: forceShrink,
              }}
              autoComplete={autoComplete}
              label={shrink ? label : null}
              color={error?.message ? 'error' : 'primary'}
              multiline={multiline}
              disabled={disabled}
              error={Boolean(error?.message)}
              sx={{
                ...textFieldLabelStyles,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: error?.message
                    ? `1px solid ${errorColors.main} !important`
                    : `1px solid ${other.borderLight} !important`,
                },
                ...(textColor && {
                  '& .Mui-focused': {
                    color: textColor,
                  },
                  '& textarea': {
                    overflow: 'auto !important',
                  },
                }),
              }}
              InputProps={{
                sx: multiline
                  ? { padding: '10px 5px' }
                  : {
                      height: '38px',
                      width,
                      '& .MuiInputBase-input': {
                        color: showNegativeValueIndicator
                          ? textColorInput
                          : 'inherit',
                      },
                    },
                startAdornment,
                endAdornment,
              }}
              placeholder={placeholder}
              size="medium"
              fullWidth
              data-testid={`form-${name}-input-field`}
              {...rest}
            />
          )}
          {invalid && showError && (
            <Typography
              variant="caption"
              color="error"
              display="block"
              data-testid={`form-${name}-input-error`}
            >
              {error?.message}
            </Typography>
          )}
        </>
      )}
    />
  </Box>
);
